import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IHeadingBlockData } from '../../types/content-block-data';
import pageBuilderStore from '../../stores/pageBuilderStore';
import contentManager from '../../stores/content-manager';
import blocksManager from '../../stores/blocks-manager';
import { extractCustomFieldsFromHTML } from '../../lib/page-builder-lib';

/**
 * Converts a HeadingBlock (IContentBlock) to an HTML string.
 * Uses heading level from data (h1 - h6).
 *
 * @param block - The IContentBlock (should be blockType "heading")
 * @returns An HTML heading string (e.g. <h2>Section Title</h2>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromHeadingBlockToHTML(block: IContentBlock): string {
	if (block.blockType !== "heading") return "";

	const isEmail = pageBuilderStore.getIsEmail();
	const data = block.blockData as IHeadingBlockData;
	const level = Math.min(Math.max(data.headingLevel ?? 1, 1), 6);

	let headingStyle = `color: ${data.color || "#ffffff"}; `;
	headingStyle += `font-family: ${data.fontFamily || "Inter, sans-serif"}; `;
	headingStyle += `font-size: ${data.fontSize?.value ? `${data.fontSize.value}${data.fontSize.unit || "rem"}` : "1rem"}; `;
	headingStyle += `font-weight: ${data.fontWeight || "700"}; `;
	headingStyle += `line-height: ${data.lineHeight?.value ? `${data.lineHeight.value}${data.lineHeight.unit || ""}` : "normal"};`;
	headingStyle += `letter-spacing: ${data.letterSpacing?.value ? `${data.letterSpacing.value}${data.letterSpacing.unit || "px"}` : "normal"};`;
	headingStyle += `text-decoration: ${data.textDecoration || "none"};`;
	headingStyle += `text-transform: ${data.textTransform || "none"};`;
	headingStyle += `text-align: ${data.alignment || "left"};`;
	headingStyle += `transition: all 0.15s;`;

	const headerOpenTag = `<h${level} class="${block.selector}" style="${headingStyle}">`;
	const headerCloseTag = `</h${level}>`;
	const htmlForExport = pageBuilderStore.getIsEmail() 
		? data.html.replace(
  		/<span[^>]*data-custom-field="true"[^>]*data-field-value="({.*?})"[^>]*contenteditable="false"[^>]*>.*?<\/span>/g,
  		'$1'
		) : data.html;
	return isEmail ? `<tr><td style="padding-bottom: 20px;">${headerOpenTag}${htmlForExport ?? ""}${headerCloseTag}</td></tr>` : `${headerOpenTag}${htmlForExport ?? ""}${headerCloseTag}`;
}

/**
 * Serializes a HeadingBlock's data for storage or export.
 * Omits UI-only fields
 *
 * @param block - The IContentBlock (should be blockType "heading")
 * @returns A JSON-serializable object
 */
export function fromHeadingBlockToJSON(block: IContentBlock): object {
	if (block.blockType !== "heading") return {};
	const data = block.blockData as IHeadingBlockData;

	return {
		contentBlockId: block.contentBlockId,
		blockType: block.blockType,
		selector: block.selector,
		blockData: {
			html: data.html ?? "",
			headingLevel: data.headingLevel ?? 1,
		},
		// Extend with heading-specific fields if needed in the future
	};
}

/**
 * Reconstructs a HeadingBlock from JSON data.
 *
 * @param json - The JSON object to parse.
 * @returns An IContentBlock instance (for use in our store/UI)
 */
export function fromJSONToHeadingBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
	const cssContent = contentManager.getStylesForSelector(json.selector);
  
  const newDesignBlockRef = createRef<HTMLDivElement>();
	const newContentBlockRef = createRef<HTMLElement>(); // generic for now

	const block: IDesignBlock = {
		designBlockId: crypto.randomUUID(), // always generate fresh
		designBlockRef: newDesignBlockRef,
		contentBlock: {
			contentBlockId: json.contentBlockId,
			blockType: json.blockType,
			contentBlockRef: newContentBlockRef, // Will be assigned in UI logic
			selector: json.selector,
			blockData: {
				html: json.blockData.html ?? "",
				headingLevel: json.blockData.headingLevel ?? 1,
				fontFamily: cssContent?.["font-family"] ?? "Inter, sans-serif",
				fontSize: blocksManager.parseDimensions(cssContent?.["font-size"], "single"),
				fontWeight: cssContent?.["font-weight"] ?? "500",
				color: cssContent!["color"] ?? "#222222",
				lineHeight: blocksManager.parseDimensions(cssContent?.["line-height"], "single"),
				letterSpacing: blocksManager.parseDimensions(cssContent?.["letter-spacing"], "single"),
				textDecoration: cssContent?.["text-decoration"] ?? "none",
				textTransform: cssContent?.["text-transform"] ?? "none",
				alignment: cssContent?.["text-align"] ?? "left",
			} as IHeadingBlockData,
		} as IContentBlock
	};

	blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

	// ✅ Hydrate addedFields for this block on load (email + edit mode only)
  if (pageBuilderStore.getIsEmail() && pageBuilderStore.getIsEditingContent()) {
    const html = (block.contentBlock.blockData as IHeadingBlockData).html || "";
    const tokens = pageBuilderStore.getCustomFields();
    const fields = extractCustomFieldsFromHTML(html, tokens);
    if (fields.length) {
      // merge with any existing (defensive), then dedupe
      const existing = pageBuilderStore.getAddedFields(json.contentBlockId) || [];
      const merged = Array.from(new Set([...existing, ...fields]));
      pageBuilderStore.updateAddedFields(json.contentBlockId, merged);
    }
  }

	return block;
}

